import { useState, useEffect } from "react";
import { Search, Monitor } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GlassCard } from "@/components/ui/glass-card";
import { AppleDeviceIcon } from "@/components/icons";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";

interface Device {
  id: string;
  name: string;
  hostname: string | null;
  os_type: string | null;
  os_version: string | null;
  status: string | null;
  last_seen: string | null;
  battery_level: number | null;
}

interface TagDevicesTabProps {
  tagId: string;
  tagName?: string;
}

export const TagDevicesTab = ({ tagId, tagName }: TagDevicesTabProps) => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        setIsLoading(true);

        const { data, error } = await supabase
          .from('device_tags')
          .select(`
            device_id,
            devices (
              id,
              name,
              hostname,
              os_type,
              os_version,
              status,
              last_seen,
              battery_level
            )
          `)
          .eq('tag_id', tagId);

        if (error) {
          console.error('Error fetching devices:', error);
          return;
        }

        const deviceList = data?.map(item => item.devices).filter(Boolean) as Device[];
        setDevices(deviceList || []);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDevices();
  }, [tagId]);

  const filteredDevices = devices.filter(device =>
    device.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    device.hostname?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <GlassCard key={i} variant="subtle" hover={false} className="p-4 animate-pulse">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-muted"></div>
              <div className="flex-1 space-y-1">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </div>
            </div>
            <div className="space-y-1.5">
              <div className="h-3 bg-muted rounded"></div>
              <div className="h-3 bg-muted rounded w-2/3"></div>
              <div className="h-3 bg-muted rounded w-3/4"></div>
            </div>
          </GlassCard>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search devices..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
      </div>

      {filteredDevices.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Monitor className="h-16 w-16 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium text-muted-foreground mb-2">No Devices Found</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            {searchQuery 
              ? "No devices match your search criteria. Try adjusting your search terms."
              : "This tag has no devices assigned to it yet. Devices will appear here when they are tagged."
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredDevices.map((device) => (
            <GlassCard key={device.id} variant="subtle" className="p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <AppleDeviceIcon osType={device.os_type || 'macOS'} modelName={device.name} className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <Link 
                    to={`/devices/${device.id}?from=tag&tagId=${tagId}&tagName=${encodeURIComponent(tagName || 'Tag')}`}
                    className="font-medium text-sm hover:text-primary transition-colors block truncate"
                  >
                    {device.name}
                  </Link>
                  {device.hostname && (
                    <p className="text-xs text-muted-foreground truncate">
                      {device.hostname}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="space-y-1.5 text-xs">
                {device.os_type && (
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">OS</span>
                    <span className="font-medium">{device.os_type} {device.os_version}</span>
                  </div>
                )}
                
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Status</span>
                  <Badge 
                    variant={
                      device.status === 'online' ? 'default' : 
                      device.status === 'offline' ? 'destructive' : 
                      'secondary'
                    }
                    className="text-xs h-5"
                  >
                    {device.status || 'unknown'}
                  </Badge>
                </div>
                
                {device.battery_level !== null && (
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Battery</span>
                    <span className="font-medium">{device.battery_level}%</span>
                  </div>
                )}
                
                {device.last_seen && (
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Last Seen</span>
                    <span className="font-medium">{new Date(device.last_seen).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
};